import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import { Html5Qrcode } from 'html5-qrcode';
import {
    UserCheck, Camera, ScanLine, ImagePlus, X, Loader2, Trash2, Check,
    RefreshCw, AlertTriangle, Ticket, History, Send, QrCode, Printer, Phone,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import BetDateSelector from '../../components/BetDateSelector';
import LotteryBatchScan from '../../components/LotteryBatchScan';
import LotteryScanHistory from '../../components/admin/LotteryScanHistory';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const STATES = ['IL', 'MS', 'TN'];
const STATE_NAMES = { IL: 'Illinois', MS: 'Mississippi', TN: 'Tennessee' };
const MAX_BYTES = 6 * 1024 * 1024;

// localStorage cache of the last N customers this admin/device placed tickets
// for. One-tap re-select — invaluable during a lunch rush when regulars come
// back same day. Per-device (admin staff may share a kiosk, that's fine).
const RECENTS_KEY = 'alh-recent-customers';
const RECENTS_MAX = 5;

const loadRecents = () => {
    try {
        const raw = localStorage.getItem(RECENTS_KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.slice(0, RECENTS_MAX) : [];
    } catch { return []; }
};

const pushRecent = (u) => {
    if (!u?.id) return;
    try {
        const current = loadRecents().filter(r => r.id !== u.id);
        current.unshift({
            id: u.id,
            name: u.name || u.email || 'Customer',
            phone: u.phone || '',
        });
        localStorage.setItem(RECENTS_KEY, JSON.stringify(current.slice(0, RECENTS_MAX)));
    } catch { /* quota / private-mode — non-fatal */ }
};

const fmtCT = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        }) + ' CT';
    } catch { return ''; }
};

/**
 * Admin Lottery Ticket Helper — pick a customer, scan their slip (or
 * type the numbers manually), and submit on their behalf.
 *
 * Each placed ticket is debited from THEIR balance and tagged with
 * placed_by_admin so we have a full audit trail.
 *
 * Reused on operator + cashier dashboards via the `playersEndpoint` prop
 * which controls which players are listed in the picker.
 */
export const AdminLotteryHelperPanel = ({ playersEndpoint = '/admin/users' } = {}) => {
    // 'single' = pick one customer & place; 'batch' = scan many tickets fast
    const [mode, setMode] = useState('single');
    // Customer picker
    const [users, setUsers] = useState([]);
    const [query, setQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [usersLoading, setUsersLoading] = useState(false);

    // Scan state
    const [scanning, setScanning] = useState(false);
    const [previewSrc, setPreviewSrc] = useState('');
    const [bets, setBets] = useState([]);
    const [notes, setNotes] = useState('');
    const [scanId, setScanId] = useState(null);
    const [bulkDates, setBulkDates] = useState(null);  // isolated bulk-apply state
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    // Submission state
    const [submitting, setSubmitting] = useState(false);

    // History
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Recent customers (per-device localStorage)
    const [recents, setRecents] = useState(() => loadRecents());

    // QR scanner state
    const [qrOpen, setQrOpen] = useState(false);
    const [qrCardOpen, setQrCardOpen] = useState(false);
    const qrScannerRef = useRef(null);

    // Normalize a phone string to just digits for fuzzy matching
    const phoneDigits = (s) => (s || '').replace(/\D+/g, '');

    // QR payload helpers — we encode a lightweight JSON so future
    // server-side QR cards can verify, but on the admin side we only
    // need the user_id to auto-select the customer.
    const buildQrPayload = (u) => JSON.stringify({
        type: 'betohalics-customer',
        v: 1,
        id: u.id,
        name: u.name || u.email,
        phone: u.phone || '',
    });

    /**
     * Player roster loader.
     *
     * Why we re-fetch on every keystroke instead of filtering a cached list:
     * the backend caps each /cashier/players (and /admin/users) response at
     * 50 rows. If the cashier had >50 players in their visible roster, the
     * desired customer would never be in the cache, so the search box "did
     * nothing" — exactly the bug the user reported.
     *
     * We debounce 200ms so it's still snappy, and we pass the query as `?q=`.
     */
    const loadUsers = useCallback(async (searchQ = '') => {
        setUsersLoading(true);
        try {
            const url = `${API}${playersEndpoint}${searchQ ? `?q=${encodeURIComponent(searchQ)}` : ''}`;
            const { data } = await axios.get(url);
            setUsers(Array.isArray(data) ? data : []);
        } catch { toast.error('Could not load player list'); }
        setUsersLoading(false);
    }, [playersEndpoint]);

    const loadHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/lottery/placed-by-admin?limit=15`);
            setHistory(data.rows || []);
        } catch { /* non-fatal */ }
        setHistoryLoading(false);
    }, []);

    useEffect(() => { loadUsers(); loadHistory(); }, [loadUsers, loadHistory]);

    // Debounce search input → re-fetch from server so cashiers with >50
    // players still get hits. Local filter still runs on top for instant
    // feedback as you type.
    useEffect(() => {
        const handle = setTimeout(() => {
            loadUsers(query.trim());
        }, 220);
        return () => clearTimeout(handle);
    }, [query, loadUsers]);

    const filteredUsers = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = users
            .filter(u => u.status !== 'archived')
            .sort((a, b) => (b.last_load_at || '').localeCompare(a.last_load_at || ''));
        if (!q) return list.slice(0, 30);
        const qDigits = phoneDigits(q);
        return list.filter(u => {
            const nameMatch = (u.name || '').toLowerCase().includes(q);
            const emailMatch = (u.email || '').toLowerCase().includes(q);
            const phoneMatch = qDigits && phoneDigits(u.phone).includes(qDigits);
            return nameMatch || emailMatch || phoneMatch;
        }).slice(0, 30);
    }, [users, query]);

    // Select a recent customer by id — looks up the latest full user record
    // from the loaded roster so we get fresh balance/credits.
    const selectRecent = (recent) => {
        const fresh = users.find(u => u.id === recent.id);
        if (fresh) {
            setSelectedUser(fresh);
            setQuery('');
            return;
        }
        // Roster not yet loaded — fall back to the cached recent snapshot.
        setSelectedUser({
            id: recent.id,
            name: recent.name,
            phone: recent.phone,
            credits: 0,
            bonus_credits: 0,
        });
        toast.message('Loaded from cache — balances will refresh shortly.');
    };

    const removeRecent = (id) => {
        try {
            const current = loadRecents().filter(r => r.id !== id);
            localStorage.setItem(RECENTS_KEY, JSON.stringify(current));
            setRecents(current);
        } catch { /* non-fatal */ }
    };


    // Try to read a customer QR payload — accepts our JSON or a bare ObjectId
    const consumeQrPayload = useCallback((raw) => {
        if (!raw) return false;
        let uid = null;
        try {
            const data = JSON.parse(raw);
            if (data && data.id) uid = String(data.id);
        } catch {
            // Bare ObjectId / plain string fallback
            if (/^[a-f0-9]{24}$/i.test(raw.trim())) uid = raw.trim();
        }
        if (!uid) {
            toast.error('QR code is not a customer card.');
            return false;
        }
        const match = users.find(u => u.id === uid);
        if (!match) {
            toast.error('Customer not found in roster. Refresh and try again.');
            return false;
        }
        setSelectedUser(match);
        setQuery('');
        toast.success(`Selected ${match.name || match.email}`);
        return true;
    }, [users]);

    // Start / stop the camera-based QR scanner
    useEffect(() => {
        if (!qrOpen) {
            // Stop existing scanner
            if (qrScannerRef.current) {
                try { qrScannerRef.current.stop().catch(() => { }); } catch { /* ignore */ }
                try { qrScannerRef.current.clear(); } catch { /* ignore */ }
                qrScannerRef.current = null;
            }
            return undefined;
        }
        // Wait a frame so the DOM node exists
        const timer = setTimeout(() => {
            const elementId = 'alh-qr-reader';
            const node = document.getElementById(elementId);
            if (!node) return;
            const scanner = new Html5Qrcode(elementId, /* verbose */ false);
            qrScannerRef.current = scanner;
            scanner.start(
                { facingMode: 'environment' },
                { fps: 10, qrbox: { width: 240, height: 240 } },
                (decodedText) => {
                    if (consumeQrPayload(decodedText)) {
                        setQrOpen(false);
                    }
                },
                () => { /* swallow per-frame decode failures */ },
            ).catch((err) => {
                console.warn('QR scanner start failed:', err);
                // Replace the empty scanner container with a hint and KEEP the
                // dialog open so the operator can still use the upload fallback.
                if (node) {
                    node.innerHTML = `
                        <div style="padding:24px;text-align:center;color:#A0AAB2;font-size:12px;">
                            <strong style="display:block;color:#FFD700;margin-bottom:6px;">Camera unavailable</strong>
                            Use "Upload a QR image instead" below — or type the customer's phone in the search box.
                        </div>
                    `;
                }
                toast.error('Camera unavailable — use the upload option below.');
            });
        }, 100);

        return () => clearTimeout(timer);
    }, [qrOpen, consumeQrPayload]);

    const handleQrImage = (file) => {
        if (!file) return;
        const scanner = new Html5Qrcode('alh-qr-reader-static', false);
        scanner.scanFile(file, true)
            .then((text) => {
                // Only close the dialog if the QR actually matched a customer —
                // mirrors the camera onDecode handler so a misread doesn't
                // strand the operator.
                if (consumeQrPayload(text)) setQrOpen(false);
            })
            .catch(() => toast.error('Could not read a QR code from that image.'));
    };

    const printQrCard = () => {
        if (!selectedUser) return;
        const payload = buildQrPayload(selectedUser);
        const w = window.open('', '_blank', 'width=520,height=700');
        if (!w) { toast.error('Pop-up blocked — allow pop-ups to print.'); return; }
        // Generate a self-contained printable card with the QR encoded as an
        // SVG via an external lib-free path: we draw via a hidden canvas
        // already in the React tree (in the dialog) — but to make the print
        // page truly standalone we'll use a Google Chart QR URL as fallback.
        const safeName = (selectedUser.name || selectedUser.email || 'Customer').replace(/[<>"]/g, '');
        const phone = selectedUser.phone || '';
        const encoded = encodeURIComponent(payload);
        w.document.write(`<!doctype html>
<html><head><title>Betohalics Customer Card</title>
<style>
  @page { size: 3.5in 2in; margin: 0; }
  body { font-family: -apple-system, system-ui, sans-serif; margin: 0; padding: 0; background: #fff; color: #000; }
  .card { width: 3.5in; height: 2in; box-sizing: border-box; padding: 12px; display: flex; gap: 10px; align-items: center; border: 1px solid #333; border-radius: 10px; }
  .left { flex: 1; min-width: 0; }
  h1 { font-size: 13pt; margin: 0 0 4px; line-height: 1.1; word-break: break-word; }
  p  { font-size: 9pt; margin: 0; color: #333; }
  .brand { font-size: 7pt; letter-spacing: 1px; text-transform: uppercase; margin-top: 6px; color: #666; }
  img { width: 1.4in; height: 1.4in; }
  @media print { .nope { display: none; } }
</style></head><body>
<div class="card">
  <div class="left">
    <h1>${safeName}</h1>
    <p>${phone}</p>
    <p class="brand">Betohalics · Customer Card</p>
  </div>
  <img alt="QR" src="https://api.qrserver.com/v1/create-qr-code/?data=${encoded}&size=240x240&margin=0" />
</div>
<div class="nope" style="text-align:center; padding:12px; font-size:11pt;">
  <button onclick="window.print()" style="padding:8px 16px; font-weight:bold;">Print card</button>
</div>
<script>window.onload = function() { setTimeout(function() { window.print(); }, 400); };</script>
</body></html>`);
        w.document.close();
    };

    const handleFile = useCallback(async (file) => {
        if (!file) return;
        if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
            toast.error('Only JPG, PNG, or WEBP images supported.');
            return;
        }
        if (file.size > MAX_BYTES) {
            toast.error('Image too large.');
            return;
        }
        const reader = new FileReader();
        reader.onload = async (ev) => {
            const dataUrl = ev.target.result;
            setPreviewSrc(dataUrl);
            const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
            setScanning(true);
            setBets([]);
            setNotes('');
            try {
                const { data } = await axios.post(`${API}/admin/lottery/scan`, {
                    image_base64: base64,
                    mime_type: file.type,
                });
                const seeded = (data?.bets || []).map((b, i) => ({
                    ...b,
                    id: `${Date.now()}-${i}`,
                    selected: true,
                }));
                setBets(seeded);
                setNotes(data?.notes || '');
                setScanId(data?.scan_id || null);
                if (seeded.length === 0) toast.warning('No bets detected. Try another photo.');
                else toast.success(`Found ${seeded.length} bet${seeded.length === 1 ? '' : 's'} — review & submit.`);
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Scan failed');
            }
            setScanning(false);
        };
        reader.readAsDataURL(file);
    }, []);

    const updateBet = (id, patch) => setBets(prev => prev.map(b => b.id === id ? { ...b, ...patch } : b));
    const removeBet = (id) => setBets(prev => prev.filter(b => b.id !== id));
    const setDigit = (id, idx, val) => {
        const v = val === '' ? 0 : Math.max(0, Math.min(9, parseInt(val, 10) || 0));
        setBets(prev => prev.map(b => {
            if (b.id !== id) return b;
            const digits = [...b.digits]; digits[idx] = v;
            return { ...b, digits };
        }));
    };

    const addManualBet = () => {
        setBets(prev => [...prev, {
            id: `manual-${Date.now()}`,
            game_type: 'Pick 3',
            digits: [0, 0, 0],
            bet_type: 'straight',
            amount: 1,
            state: 'TN',
            drawing_time: 'evening',
            confidence: 1,
            selected: true,
        }]);
    };

    const resetAll = () => {
        setBets([]); setNotes(''); setPreviewSrc(''); setScanning(false); setBulkDates(null);
    };

    const validBets = bets.filter(b => b.selected);

    const submitAll = async () => {
        if (!selectedUser) { toast.error('Pick a customer first.'); return; }
        if (validBets.length === 0) { toast.error('No bets selected.'); return; }
        setSubmitting(true);
        let okCount = 0;
        const errors = [];
        for (const b of validBets) {
            try {
                if (b.game_type === 'Big 3') {
                    await axios.post(`${API}/admin/lottery/big3-for`, {
                        user_id: selectedUser.id,
                        numbers: b.digits,
                        drawing_time: b.drawing_time,
                        ...(b.dates && b.dates.length > 0 ? { dates: b.dates } : {}),
                        ...(scanId ? { scan_id: scanId } : {}),
                    });
                } else {
                    const pickType = b.game_type === 'Pick 4' ? 4 : 3;
                    // "Combo / Ways" → fan out into N straight tickets
                    const endpoint = b.play_as_ways
                        ? `${API}/admin/lottery/ticket-for-ways`
                        : `${API}/admin/lottery/ticket-for`;
                    await axios.post(endpoint, {
                        user_id: selectedUser.id,
                        state: b.state,
                        pick_type: pickType,
                        numbers: b.digits,
                        bet_type: b.play_as_ways ? 'straight' : b.bet_type,
                        amount: Number(b.amount) || 1,
                        drawing_time: b.drawing_time,
                        ...(b.dates && b.dates.length > 0 ? { dates: b.dates } : {}),
                        ...(scanId ? { scan_id: scanId } : {}),
                    });
                }
                okCount += 1;
            } catch (e) {
                errors.push(`${b.game_type} ${b.digits.join('-')}: ${e.response?.data?.detail || 'failed'}`);
            }
        }
        setSubmitting(false);

        if (okCount > 0) {
            toast.success(`Placed ${okCount} bet${okCount === 1 ? '' : 's'} for ${selectedUser.name || selectedUser.email}.`);
            pushRecent(selectedUser);
            setRecents(loadRecents());
            resetAll();
            await loadHistory();
            await loadUsers();   // refresh balances
        }
        if (errors.length > 0) {
            toast.error(`${errors.length} failed: ${errors.slice(0, 2).join(' · ')}`);
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-lottery-helper">
            {/* Mode toggle: single customer vs fast batch scan */}
            <div className="flex gap-2 p-1 rounded-xl bg-[#0a0a0a] border border-white/10">
                <button type="button" onClick={() => setMode('single')}
                    className={`flex-1 h-9 rounded-lg text-sm font-bold transition-colors ${mode === 'single' ? 'bg-[#FF007F] text-white' : 'text-[#A0AAB2] hover:text-white'}`}
                    data-testid="lottery-mode-single">
                    Single customer
                </button>
                <button type="button" onClick={() => setMode('batch')}
                    className={`flex-1 h-9 rounded-lg text-sm font-bold transition-colors ${mode === 'batch' ? 'bg-[#39FF14] text-black' : 'text-[#A0AAB2] hover:text-white'}`}
                    data-testid="lottery-mode-batch">
                    ⚡ Batch scan
                </button>
            </div>

            {/* Photo-audit of every scan (admin/cashier scoped) */}
            <LotteryScanHistory />

            {mode === 'batch' ? (
                <LotteryBatchScan playersEndpoint={playersEndpoint} onPlaced={loadHistory} />
            ) : (
            <>
            {/* ── PICK A CUSTOMER ── */}
            <div className="card-dark p-4" style={{ borderLeft: '3px solid #FF007F' }}>
                <div className="flex items-center gap-2 mb-2">
                    <UserCheck className="w-5 h-5 text-[#FF007F]" />
                    <h3 className="text-lg font-bold text-white">Place Lottery Tickets For A Customer</h3>
                </div>
                <p className="text-xs text-[#A0AAB2] mb-3">
                    Pick the customer, scan their handwritten slip (or type manually), tweak the bets, then submit. Each ticket debits their balance and is logged to audit.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-3">
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label className="text-[11px] text-[#A0AAB2]">Phone / name / email</Label>
                            <button
                                type="button"
                                onClick={() => setQrOpen(true)}
                                className="text-[10px] font-bold uppercase tracking-wider text-[#39FF14] flex items-center gap-1 hover:underline"
                                data-testid="alh-qr-scan-btn"
                            >
                                <QrCode className="w-3 h-3" /> Scan QR
                            </button>
                        </div>
                        <div className="relative">
                            <Phone className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A0AAB2]" />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                inputMode="search"
                                autoComplete="off"
                                placeholder="Type phone digits, name, or email…"
                                className="pl-8 h-9 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                data-testid="alh-user-search"
                            />
                        </div>
                        {/* Recent customers — one-tap re-select (per-device cache) */}
                        {recents.length > 0 && (
                            <div className="mt-2" data-testid="alh-recents">
                                <p className="text-[9px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-1">Recent</p>
                                <div className="flex flex-wrap gap-1">
                                    {recents.map((r, idx) => (
                                        <span
                                            key={r.id}
                                            className="inline-flex items-center gap-1 bg-[#7C3AED]/15 border border-[#7C3AED]/40 rounded-full text-[11px] text-white pl-2 pr-1 py-0.5 hover:bg-[#7C3AED]/25 transition"
                                            data-testid={`alh-recent-${idx}`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => selectRecent(r)}
                                                className="font-bold"
                                                title={r.phone || ''}
                                                data-testid={`alh-recent-select-${idx}`}
                                            >
                                                {(r.name || '').split(' ')[0] || 'Player'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => removeRecent(r.id)}
                                                className="text-[#A0AAB2] hover:text-[#FF3B30] p-0.5"
                                                aria-label="Remove from recents"
                                                data-testid={`alh-recent-remove-${idx}`}
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="mt-2 rounded-lg border border-white/10 bg-[#0a0a0a] max-h-64 overflow-y-auto">
                            {usersLoading ? (
                                <p className="text-[11px] text-[#A0AAB2] text-center py-6">Loading…</p>
                            ) : filteredUsers.length === 0 ? (
                                <p className="text-[11px] text-[#A0AAB2] text-center py-6">No customers match</p>
                            ) : filteredUsers.map(u => {
                                const active = selectedUser?.id === u.id;
                                const playable = (Number(u.credits) || 0) + (Number(u.bonus_credits) || 0);
                                return (
                                    <button
                                        key={u.id}
                                        type="button"
                                        onClick={() => setSelectedUser(u)}
                                        className={`w-full text-left px-3 py-2 border-b border-white/5 last:border-0 transition ${active ? 'bg-[#FF007F]/15' : 'hover:bg-white/5'}`}
                                        data-testid={`alh-user-row-${u.id}`}
                                    >
                                        <p className="text-sm font-bold text-white truncate">{u.name || u.email}</p>
                                        <p className="text-[10px] text-[#A0AAB2] truncate flex items-center justify-between gap-2">
                                            <span className="truncate">{u.phone || u.email}</span>
                                            <span className="font-mono text-[#FFD700]">${playable.toFixed(2)}</span>
                                        </p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-3 min-h-[100px]">
                        {!selectedUser ? (
                            <p className="text-xs text-[#A0AAB2] text-center py-8">
                                Pick a customer on the left to start.
                            </p>
                        ) : (
                            <div>
                                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                    <div>
                                        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold">Customer</p>
                                        <p className="text-white font-black text-base">{selectedUser.name || selectedUser.email}</p>
                                        <p className="text-[10px] text-[#A0AAB2]">
                                            {selectedUser.email} · {selectedUser.phone || 'no phone'} ·
                                            <span className="text-[#FFD700] font-mono ml-1">
                                                ${((Number(selectedUser.credits) || 0) + (Number(selectedUser.bonus_credits) || 0)).toFixed(2)} playable
                                            </span>
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline" size="sm"
                                            onClick={() => setQrCardOpen(true)}
                                            className="border-[#39FF14]/40 text-[#39FF14] text-xs h-8"
                                            data-testid="alh-show-qr-btn"
                                        >
                                            <QrCode className="w-3.5 h-3.5 mr-1" /> QR Card
                                        </Button>
                                        <Button
                                            variant="outline" size="sm"
                                            onClick={() => { setSelectedUser(null); resetAll(); }}
                                            className="border-white/10 text-white text-xs h-8"
                                            data-testid="alh-clear-user"
                                        >
                                            Change
                                        </Button>
                                    </div>
                                </div>

                                {/* Upload / scan controls */}
                                {!previewSrc && bets.length === 0 && (
                                    <div className="space-y-2">
                                        <div className="rounded-xl border-2 border-dashed border-white/15 py-6 text-center bg-[#0e0e0e]">
                                            <ImagePlus className="w-8 h-8 text-[#A0AAB2] mx-auto mb-1" />
                                            <p className="text-xs text-[#A0AAB2] mb-3">Snap or upload a photo of the customer's slip</p>
                                            <div className="flex gap-2 justify-center flex-wrap">
                                                <Button
                                                    onClick={() => cameraInputRef.current?.click()}
                                                    className="bg-[#FF007F] text-white font-bold h-9"
                                                    data-testid="alh-camera-btn"
                                                >
                                                    <Camera className="w-4 h-4 mr-1" /> Camera
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    className="border-white/20 text-white h-9"
                                                    data-testid="alh-upload-btn"
                                                >
                                                    <ImagePlus className="w-4 h-4 mr-1" /> Upload
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    onClick={addManualBet}
                                                    className="border-[#39FF14]/40 text-[#39FF14] h-9"
                                                    data-testid="alh-manual-btn"
                                                >
                                                    <Ticket className="w-4 h-4 mr-1" /> Manual
                                                </Button>
                                            </div>
                                            <input
                                                ref={cameraInputRef} type="file"
                                                accept="image/*" capture="environment"
                                                onChange={(e) => handleFile(e.target.files?.[0])}
                                                className="hidden"
                                            />
                                            <input
                                                ref={fileInputRef} type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                onChange={(e) => handleFile(e.target.files?.[0])}
                                                className="hidden"
                                            />
                                        </div>
                                    </div>
                                )}

                                {previewSrc && (
                                    <div className="relative rounded-lg overflow-hidden border border-white/10 mb-3">
                                        <img src={previewSrc} alt="Scanned slip" className="w-full max-h-40 object-contain bg-[#0a0a0a]" />
                                        <button
                                            type="button"
                                            onClick={resetAll}
                                            className="absolute top-2 right-2 bg-black/60 hover:bg-black text-white rounded-full p-1"
                                            aria-label="Remove image"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}

                                {scanning && (
                                    <div className="flex items-center justify-center gap-2 py-3 text-[#A0AAB2]">
                                        <Loader2 className="w-4 h-4 animate-spin text-[#FF007F]" />
                                        <span className="text-xs">Reading slip…</span>
                                    </div>
                                )}

                                {!scanning && bets.length > 0 && (
                                    <>
                                        {notes && (
                                            <p className="text-[10px] text-[#A0AAB2] italic mb-2">AI notes: {notes}</p>
                                        )}
                                        {/* Bulk apply — set same draw date(s) for every bet. Isolated state. */}
                                        <div className="rounded-lg border border-[#7C3AED]/30 bg-[#7C3AED]/5 p-2.5 mb-2" data-testid="alh-bulk-date">
                                            <span className="text-[10px] uppercase tracking-widest text-[#7C3AED] font-black block mb-1.5">
                                                Bulk · apply to all {bets.length} bets
                                            </span>
                                            <BetDateSelector
                                                dates={bulkDates}
                                                onChange={(d) => {
                                                    setBulkDates(d);
                                                    setBets(prev => prev.map(b => ({ ...b, dates: d })));
                                                }}
                                                testIdPrefix="alh-bulk"
                                            />
                                        </div>
                                        <div className="space-y-2 mb-2">
                                            {/* Column labels — render once above all bet rows so the
                                                amount field stops getting confused with the other inputs */}
                                            <div className="hidden sm:grid grid-cols-[16px_1fr] gap-2 px-2">
                                                <span />
                                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-1 text-[9px] uppercase tracking-widest text-[#A0AAB2] font-bold">
                                                    <span>Game</span>
                                                    <span>State</span>
                                                    <span>Bet type</span>
                                                    <span className="text-[#FFD700]">$ Amount</span>
                                                    <span>Draw</span>
                                                </div>
                                            </div>
                                            <AnimatePresence initial={false}>
                                                {bets.map((b, idx) => (
                                                    <motion.div
                                                        key={b.id}
                                                        initial={{ opacity: 0, y: -4 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -4 }}
                                                        className={`rounded-lg border p-2 ${b.selected ? 'bg-[#0e0e0e] border-white/15' : 'bg-[#0e0e0e]/50 border-white/5 opacity-60'}`}
                                                        data-testid={`alh-bet-${idx}`}
                                                    >
                                                        <div className="flex items-start gap-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={b.selected}
                                                                onChange={(e) => updateBet(b.id, { selected: e.target.checked })}
                                                                className="w-4 h-4 mt-1 accent-[#39FF14]"
                                                            />
                                                            <div className="flex-1 grid grid-cols-2 sm:grid-cols-5 gap-1">
                                                                <select
                                                                    value={b.game_type}
                                                                    onChange={(e) => {
                                                                        const gt = e.target.value;
                                                                        const need = gt === 'Pick 4' ? 4 : 3;
                                                                        const digits = b.digits.slice(0, need);
                                                                        while (digits.length < need) digits.push(0);
                                                                        updateBet(b.id, {
                                                                            game_type: gt, digits,
                                                                            bet_type: gt === 'Big 3' ? 'straight' : b.bet_type,
                                                                            amount: gt === 'Big 3' ? 10 : b.amount,
                                                                        });
                                                                    }}
                                                                    className="h-7 bg-[#0a0a0a] border border-white/10 rounded text-white text-xs px-1"
                                                                >
                                                                    <option value="Pick 3">Pick 3</option>
                                                                    <option value="Pick 4">Pick 4</option>
                                                                </select>
                                                                {b.game_type !== 'Big 3' ? (
                                                                    <select
                                                                        value={b.state}
                                                                        onChange={(e) => updateBet(b.id, { state: e.target.value })}
                                                                        className="h-7 bg-[#0a0a0a] border border-white/10 rounded text-white text-xs px-1"
                                                                    >
                                                                        {STATES.map(s => <option key={s} value={s}>{STATE_NAMES[s]}</option>)}
                                                                    </select>
                                                                ) : <span className="h-7 leading-7 text-[10px] text-[#A0AAB2] italic">All 3</span>}
                                                                {b.game_type !== 'Big 3' ? (
                                                                    <select
                                                                        value={b.play_as_ways ? 'ways' : b.bet_type}
                                                                        onChange={(e) => {
                                                                            const v = e.target.value;
                                                                            if (v === 'ways') {
                                                                                updateBet(b.id, { play_as_ways: true, bet_type: 'box' });
                                                                            } else {
                                                                                updateBet(b.id, { play_as_ways: false, bet_type: v });
                                                                            }
                                                                        }}
                                                                        className="h-7 bg-[#0a0a0a] border border-white/10 rounded text-white text-xs px-1"
                                                                    >
                                                                        <option value="straight">Straight</option>
                                                                        <option value="box">Box</option>
                                                                        {(b.ways || 1) > 1 && (
                                                                            <option value="ways">Combo ({b.ways}-way)</option>
                                                                        )}
                                                                    </select>
                                                                ) : <span className="h-7 leading-7 text-[10px] text-[#A0AAB2] italic">Straight</span>}
                                                                {b.game_type !== 'Big 3' ? (
                                                                    <div className="relative">
                                                                        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[#FFD700] text-xs font-bold pointer-events-none">$</span>
                                                                        <input
                                                                            type="number" step="0.5" min="0.5" value={b.amount}
                                                                            onChange={(e) => updateBet(b.id, { amount: e.target.value })}
                                                                            title="Bet amount in dollars"
                                                                            className="h-7 pl-4 bg-[#0a0a0a] border border-[#FFD700]/30 rounded text-[#FFD700] text-xs font-bold px-1 w-full focus:border-[#FFD700] focus:outline-none"
                                                                            data-testid={`alh-bet-amount-${idx}`}
                                                                        />
                                                                    </div>
                                                                ) : <span className="h-7 leading-7 text-xs text-[#FFD700] font-bold text-center">$10 fixed</span>}
                                                                <select
                                                                    value={b.drawing_time}
                                                                    onChange={(e) => updateBet(b.id, { drawing_time: e.target.value })}
                                                                    className="h-7 bg-[#0a0a0a] border border-white/10 rounded text-white text-xs px-1"
                                                                >
                                                                    <option value="midday">Midday</option>
                                                                    <option value="evening">Evening</option>
                                                                </select>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeBet(b.id)}
                                                                className="p-1 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded"
                                                                aria-label="Remove"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                        <div className="flex gap-1 mt-2 ml-6">
                                                            {b.digits.map((d, i) => (
                                                                <input
                                                                    key={i}
                                                                    type="number" min="0" max="9" value={d}
                                                                    onChange={(e) => setDigit(b.id, i, e.target.value)}
                                                                    className="w-9 h-9 text-center text-base font-bold bg-[#0a0a0a] border border-white/15 rounded text-white"
                                                                />
                                                            ))}
                                                            <span className={`ml-auto self-end text-[10px] font-mono ${b.confidence > 0.75 ? 'text-[#39FF14]' : b.confidence > 0.5 ? 'text-[#FFD700]' : 'text-[#A0AAB2]'}`}>
                                                                {Math.round((b.confidence || 0) * 100)}%
                                                            </span>
                                                        </div>
                                                        <div className="mt-2 pt-2 border-t border-white/5">
                                                            <BetDateSelector
                                                                dates={b.dates}
                                                                onChange={(d) => updateBet(b.id, { dates: d })}
                                                                testIdPrefix={`alh-date-${idx}`}
                                                            />
                                                        </div>

                                                        {/* Per-bet Risk → Win badge — makes combo rows pop visually */}
                                                        {b.game_type !== 'Big 3' && (() => {
                                                            const pt = b.game_type === 'Pick 4' ? 4 : 3;
                                                            const payouts = pt === 3
                                                                ? { straight: 600, box: 150 }
                                                                : { straight: 5000, box: 1250 };
                                                            const amt = Number(b.amount) || 0;
                                                            const ways = Math.max(1, Number(b.ways) || 1);
                                                            let label, risk, perDraw, accent;
                                                            if (b.play_as_ways) {
                                                                label = `COMBO · ${ways}-WAY`;
                                                                accent = 'from-[#FFD700] to-[#FF007F]';
                                                                risk = amt * ways;
                                                                perDraw = amt * payouts.straight;
                                                            } else if (b.bet_type === 'box') {
                                                                label = `BOX${ways > 1 ? ` · ${ways}-WAY` : ''}`;
                                                                accent = 'from-[#A855F7] to-[#A855F7]';
                                                                risk = amt;
                                                                perDraw = amt * payouts.box;
                                                            } else {
                                                                label = 'STRAIGHT';
                                                                accent = 'from-[#39FF14] to-[#39FF14]';
                                                                risk = amt;
                                                                perDraw = amt * payouts.straight;
                                                            }
                                                            const numDays = Math.max(1, (b.dates?.length || 1));
                                                            const riskTotal = risk * numDays;
                                                            return (
                                                                <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between gap-2"
                                                                     data-testid={`alh-towin-${idx}`}>
                                                                    <span className={`text-[10px] font-black tracking-wider px-2 py-1 rounded text-black bg-gradient-to-r ${accent}`}>
                                                                        {label}
                                                                    </span>
                                                                    <div className="flex items-center gap-1.5 text-xs">
                                                                        <span className="text-[#A0AAB2]">Risk</span>
                                                                        <span className="text-white font-mono font-bold">${riskTotal.toFixed(2)}</span>
                                                                        <span className="text-[#A0AAB2]">→</span>
                                                                        <span className="text-[#A0AAB2] uppercase text-[9px] font-bold">Win</span>
                                                                        <span className="text-[#39FF14] font-mono font-black">${perDraw.toFixed(2)}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}
                                                    </motion.div>
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                        <div className="flex gap-2 flex-wrap">
                                            <Button
                                                variant="outline" size="sm"
                                                onClick={addManualBet}
                                                className="border-[#39FF14]/40 text-[#39FF14] h-8 text-xs"
                                            >
                                                + Add bet
                                            </Button>
                                            <Button
                                                variant="outline" size="sm"
                                                onClick={resetAll}
                                                className="border-white/10 text-white h-8 text-xs"
                                            >
                                                Reset
                                            </Button>
                                            <Button
                                                onClick={submitAll}
                                                disabled={submitting || validBets.length === 0}
                                                className="ml-auto h-9 font-black text-white"
                                                style={{ background: 'linear-gradient(135deg, #FF007F, #7C3AED)' }}
                                                data-testid="alh-submit-btn"
                                            >
                                                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                                                Submit {validBets.length} for {selectedUser.name?.split(' ')[0] || 'player'}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── HISTORY ── */}
            <div className="card-dark p-4">
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-bold text-white flex items-center gap-2">
                        <History className="w-4 h-4 text-[#FFD700]" /> Recent admin-placed tickets
                    </h4>
                    <Button variant="outline" size="sm" onClick={loadHistory} disabled={historyLoading} className="border-white/10 text-white h-7 text-xs">
                        <RefreshCw className={`w-3 h-3 mr-1 ${historyLoading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                </div>
                <div className="rounded-lg border border-white/10 overflow-hidden">
                    <table className="w-full text-xs">
                        <thead className="bg-[#0e0e0e] text-[#A0AAB2] uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-2 py-1.5">When</th>
                                <th className="text-left px-2 py-1.5">Customer</th>
                                <th className="text-left px-2 py-1.5">Bet</th>
                                <th className="text-right px-2 py-1.5">$</th>
                                <th className="text-left px-2 py-1.5">By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.length === 0 ? (
                                <tr><td colSpan={5} className="text-center text-[#A0AAB2] py-3 text-[11px]">No admin-placed tickets yet.</td></tr>
                            ) : history.map(t => {
                                const isBig3 = t.bet_type === 'big3';
                                const dt = (t.drawing_time || '').toLowerCase();
                                const isEvening = dt === 'evening';
                                const drawChip = (
                                    <span
                                        className={`ml-1 text-[9px] px-1.5 py-0.5 rounded font-black uppercase tracking-wider ${
                                            isEvening
                                                ? 'bg-[#1E3A8A]/40 text-[#93C5FD] border border-[#3B82F6]/40'
                                                : 'bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30'
                                        }`}
                                    >
                                        {isEvening ? 'Evening' : 'Midday'}
                                    </span>
                                );
                                return (
                                    <tr key={t.id} className="border-t border-white/5">
                                        <td className="px-2 py-1 text-[#A0AAB2] whitespace-nowrap">{fmtCT(t.created_at)}</td>
                                        <td className="px-2 py-1 text-white truncate max-w-[140px]">{t.user_name}</td>
                                        <td className="px-2 py-1 text-white">
                                            {isBig3 ? (
                                                <span><span className="text-[#A855F7] font-bold">Big 3</span> {t.numbers.join('-')} {drawChip}</span>
                                            ) : (
                                                <span>{t.state} P{t.pick_type} {t.numbers.join('-')} <span className="text-[#A0AAB2]">{t.bet_type === 'box' ? 'BX' : 'STR'}</span> {drawChip}</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-1 text-right text-[#39FF14] font-mono">${Number(t.amount).toFixed(2)}</td>
                                        <td className="px-2 py-1 text-[#A0AAB2] truncate max-w-[160px]">{t.placed_by_admin_email}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="text-[10px] text-[#666] mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Every admin-placed ticket is logged to <span className="font-mono text-[#A0AAB2]">audit_logs</span>.
                </div>
            </div>

            {/* ── QR SCAN DIALOG ── */}
            <Dialog open={qrOpen} onOpenChange={setQrOpen}>
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-md" data-testid="alh-qr-scan-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <QrCode className="w-5 h-5 text-[#39FF14]" />
                            Scan Customer Card
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Point the camera at the customer's printed QR card. Or upload a photo of one.
                        </DialogDescription>
                    </DialogHeader>
                    <div id="alh-qr-reader" className="rounded-lg overflow-hidden border border-white/10 bg-black" style={{ minHeight: 280 }} />
                    <div id="alh-qr-reader-static" style={{ display: 'none' }} />
                    <div className="text-center">
                        <label className="inline-flex items-center gap-2 text-xs text-[#A0AAB2] cursor-pointer hover:text-white">
                            <ImagePlus className="w-3.5 h-3.5" />
                            Upload a QR image instead
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleQrImage(e.target.files?.[0])}
                                className="hidden"
                                data-testid="alh-qr-upload-input"
                            />
                        </label>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── PRINTABLE QR CARD ── */}
            <Dialog open={qrCardOpen} onOpenChange={setQrCardOpen}>
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-sm" data-testid="alh-qr-card-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <QrCode className="w-5 h-5 text-[#39FF14]" />
                            Customer QR Card
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Hand this to the customer — staff can scan it next time to skip the lookup.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedUser && (
                        <div className="space-y-3">
                            <div className="bg-white rounded-xl p-4 flex items-center gap-4">
                                <div className="text-black min-w-0 flex-1">
                                    <p className="font-black text-base leading-tight truncate">
                                        {selectedUser.name || selectedUser.email}
                                    </p>
                                    {selectedUser.phone && (
                                        <p className="text-xs text-gray-700">{selectedUser.phone}</p>
                                    )}
                                    <p className="text-[9px] uppercase tracking-widest text-gray-500 mt-1">
                                        Betohalics · Customer Card
                                    </p>
                                </div>
                                <QRCodeCanvas
                                    value={buildQrPayload(selectedUser)}
                                    size={120}
                                    level="M"
                                    includeMargin={false}
                                />
                            </div>
                            <Button
                                onClick={printQrCard}
                                className="w-full bg-[#39FF14] text-black hover:bg-[#39FF14]/90 font-black h-10"
                                data-testid="alh-qr-print-btn"
                            >
                                <Printer className="w-4 h-4 mr-2" /> Print card
                            </Button>
                            <p className="text-[10px] text-[#A0AAB2] text-center">
                                The card encodes only the customer's name, phone, and account ID — no balance or password info.
                            </p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            </>
            )}
        </div>
    );
};

export default AdminLotteryHelperPanel;
